import { Eye, EyeOff } from "lucide-react";
import React from "react";

import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { useTheme } from "@mui/material";
import { useDispatch } from "@src/store";
import { ExperienceBuilderAction } from "@src/store/slices/experienceBuilder";
import { EmbeddedMod } from "@src/store/slices/experienceBuilder/types";

import { useIsModHidden } from "@src/features/experienceBuilder/modifications/common/useIsModHidden";

interface VisibilityButtonProps {
  mod: EmbeddedMod;
}

/**
 * Toggles one mod in and out of the merchant's own preview. Rendered by the
 * shared row for every mod type, so a new type cannot ship without it.
 *
 * Always visible, on hidden and visible rows alike: on a hidden row it is the
 * only thing that says "this mod is hidden" once the pointer leaves, and the
 * only way back.
 */
export const VisibilityButton = ({ mod }: VisibilityButtonProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isHidden = useIsModHidden(mod.id);

  return (
    <IgIconButton
      aria-label={isHidden ? "show modification" : "hide modification"}
      color="primary"
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        // Direction comes from what this row rendered, so the click cannot
        // disagree with the icon the merchant just saw.
        void dispatch(
          ExperienceBuilderAction.setModsHidden({
            modificationIds: [mod.id],
            hidden: !isHidden,
          })
        );
      }}
    >
      {/*
       * `light`, not `dark`: the names describe the BACKGROUND the icon sits
       * on, not the icon. `dark` is #FAFAFA — for icons on a dark surface, and
       * invisible on this row.
       */}
      {isHidden ? (
        <EyeOff size={16} color={theme.igIconColors.light} />
      ) : (
        <Eye size={16} color={theme.igIconColors.light} />
      )}
    </IgIconButton>
  );
};
